'use client'
import * as React from 'react'
import {
  Block,
  isRootBlock,
  isBlockWithChildren,
  isRichTextBlock,
} from '@basehub/data/replicache/index'
import { useStorage } from './use-storage'
import { useWorkingDiff } from '~/lib/diff/use-working-diff'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { invariant } from '@epic-web/invariant'
import { useIsGuestInOthersBranch } from '~/hooks-data/use-user-role'
import { createStore } from 'zustand'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useStoreWithEqualityFn } from 'zustand/traditional'

export type StageControls = {
  allChangesAreStaged: boolean
  allChangesAreUnstaged: boolean
  stagedBlockPaths: string[]
  getStagedBlockIds: (options: { fallbackToWipBlocks?: boolean }) => string[]
  getUnstagedBlockIds: (
    options?: Partial<{
      includeNested: boolean
      includeRoot: boolean
    }>
  ) => string[]
  filterBlockIdsByStage: (
    show: 'staged' | 'unstaged',
    options?: Partial<{
      includeNested: boolean
      includeRoot: boolean
    }>
  ) => string[]
  getIsIndeterminate: (blockId: Block['id']) => boolean
  getInheritsStage: (blockId: Block['id']) => boolean
  getIsStaged: (blockId: Block['id']) => boolean
  stageBlock: (blockId: Block['id']) => void
  stageAll: () => void
  resetStagingTree: () => void
  unstageAll: () => void
  enabled: boolean
}

const STORAGE_VERSION = 2

const createStagedBlocksStore = (initial: StageControls) => {
  return createStore<StageControls>()(() => initial)
}

export type StagedBlocksStore = ReturnType<typeof createStagedBlocksStore>

export const StagedBlocksContext = React.createContext<
  StagedBlocksStore | undefined
>(undefined)

export const StagedBlocksProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const isGuestInOthersBranch = useIsGuestInOthersBranch()
  const { data } = useRepoReplicache()
  const { diff, treeNew } = useWorkingDiff()
  const [allCheckboxesAreChecked, setAllCheckboxesAreChecked] =
    React.useState(false)
  const [stagedBlockPaths, setStagedBlockPaths] = useStorage(
    'sessionStorage',
    (z) => z.array(z.string()),
    // it's fine to NOT use source block id here, as we want this sessionStorage to be unique per branch
    // for the rest, we probably want to use source block id
    `basehub-commit-stage-${STORAGE_VERSION}-${data?.repoId}-${treeNew?.root.value.id}`,
    {
      initialValue: [],
      enabled: !!data?.repoId,
    }
  )

  const treeNewMethods = React.useMemo(() => {
    if (!treeNew?.getPath) return null
    return {
      getPath: treeNew?.getPath,
      getNode: treeNew?.getNode,
    }
  }, [treeNew?.getPath, treeNew?.getNode])

  const changesTreeMethods = React.useMemo(() => {
    if (!diff?.changesTree) return null
    return {
      getNode: diff?.changesTree.getNode,
      getPath: diff?.changesTree.getPath,
    }
  }, [diff?.changesTree])

  const getSourceBlockId = React.useCallback(
    (blockId: string) => {
      const node =
        treeNewMethods?.getNode(blockId) ?? changesTreeMethods?.getNode(blockId)
      if (!node) return blockId
      return idToSourceBlockId(node.value)
    },
    [treeNewMethods, changesTreeMethods]
  )

  /** Gets path from treeNew or changesTree, and maps it to sourceBlockIds */
  const getSourceBlockPath = React.useCallback(
    (blockId: Block['id']) => {
      if (!treeNewMethods) return null
      const pathFromTreeNew = treeNewMethods.getPath(blockId, {
        format: 'array',
      })
      if (pathFromTreeNew) {
        return pathFromTreeNew
          .map((segment) =>
            idToSourceBlockId(
              treeNewMethods.getNode(segment, { required: true }).value
            )
          )
          .join('/')
      }
      if (!changesTreeMethods) return null
      const pathFromChangesTree = changesTreeMethods.getPath(blockId, {
        format: 'array',
      })
      if (pathFromChangesTree) {
        return pathFromChangesTree
          .map((segment) =>
            idToSourceBlockId(
              changesTreeMethods.getNode(segment, { required: true }).value
            )
          )
          .join('/')
      }
      return null
    },
    [changesTreeMethods, treeNewMethods]
  )

  const getIsStaged = React.useCallback(
    (blockId: Block['id']) => {
      if (!treeNew?.root.value.id) return false
      if (stagedBlockPaths.includes(treeNew.root.value.id)) {
        return true
      }

      const blockPath = getSourceBlockPath(blockId)
      if (!blockPath) return false

      const anyOfThePathSegmentIsStaged = stagedBlockPaths.some(
        (stagedPath) => {
          const stagedPathSegments = stagedPath.split('/')
          const blockPathSegments = blockPath.split('/')

          return stagedPathSegments.every((segment, index) => {
            return blockPathSegments[index] === segment
          })
        }
      )

      return anyOfThePathSegmentIsStaged
    },
    [stagedBlockPaths, getSourceBlockPath, treeNew?.root.value.id]
  )

  const getInheritsStage = React.useCallback(
    (blockId: Block['id']) => {
      if (!changesTreeMethods?.getNode) return false

      const blockPath = getSourceBlockPath(blockId)
      if (!blockPath) return false
      const isStaged = getIsStaged(blockId)
      const allBlockAscendantsAreUnmodified = blockPath
        .split('/')
        .every((segment) => {
          const blockChange = changesTreeMethods.getNode(segment)
          if (!blockChange) return true
          if (blockId === blockChange?.value.id) return true
          if (blockChange?.value.id === diff?.changesTree.root.value.id) {
            return true
          }
          return blockChange?.value.status === 'unmodified'
        })
      // if all the ascendants of this block are unmodified, then we don't inherit the stage
      if (allBlockAscendantsAreUnmodified) return false

      const blockChange = changesTreeMethods.getNode(blockId)?.value
      if (!blockChange) return false

      if (isRichTextBlock(blockChange) || !isBlockWithChildren(blockChange)) {
        return true
      }

      return (
        isStaged && !stagedBlockPaths.some((path) => path.includes(blockPath))
      )
    },
    [
      stagedBlockPaths,
      changesTreeMethods,
      getSourceBlockPath,
      getIsStaged,
      diff?.changesTree.root.value.id,
    ]
  )

  const getIsIndeterminate = React.useCallback(
    (blockId: Block['id']) => {
      const sourceBlockId = getSourceBlockId(blockId)
      return (
        !getIsStaged(blockId) &&
        stagedBlockPaths.some((path) => path.includes(sourceBlockId))
      )
    },
    [getSourceBlockId, getIsStaged, stagedBlockPaths]
  )

  const unstagePath = React.useCallback(
    (
      blockId: Block['id'],
      options: { forceStageOthers: boolean }
    ): string[] | null => {
      // recursively iterate over its parent, and so one, and if it affects blockid, then don't stage it
      // if it doesn't affect blockid, then stage it
      if (blockId === treeNew?.root.value.id) return null

      const node = changesTreeMethods?.getNode(blockId)
      invariant(node, `node for id ${blockId} could not be retrieved`)
      if (!node.parentKey) return null
      const parentNode = changesTreeMethods?.getNode(node.parentKey)
      invariant(
        parentNode,
        `parent node <${node.parentKey}> for block <${blockId}> could not be retrieved`
      )

      const siblings = (parentNode?.value.children ?? [])
        .flatMap((child) => {
          if (child.id === node.value.id) {
            return unstagePath(parentNode.value.id, options)
          }

          const pathForThisChild = getSourceBlockPath(child.id)

          if (options.forceStageOthers) {
            return pathForThisChild
          } else {
            // is staged or inherits stage, add it to the staged paths to be enforced
            if (getIsStaged(child.id) || getInheritsStage(child.id)) {
              return pathForThisChild
            }

            // has staged descendants, then return all of them
            if (getIsIndeterminate(child.id)) {
              const allDescendantsOfThisBlock = stagedBlockPaths.filter(
                (path) => path.includes(child.id)
              )
              return allDescendantsOfThisBlock
            }

            return null
          }
        })
        .filter(Boolean)

      return siblings
    },
    [
      treeNew?.root.value.id,
      changesTreeMethods,
      getSourceBlockPath,
      getIsStaged,
      getInheritsStage,
      getIsIndeterminate,
      stagedBlockPaths,
    ]
  )

  const stageBlock = React.useCallback(
    (blockId: Block['id']) => {
      const blockPath = getSourceBlockPath(blockId)
      invariant(blockPath, `blockPath for id ${blockId} could not be retrieved`)
      if (blockId === diff?.changesTree.root.value.id) return

      if (stagedBlockPaths.includes(blockPath)) {
        setStagedBlockPaths((prevStaged) => {
          const newStaged = [...(prevStaged ?? [])]
          const newStagedWithoutThisBlock = newStaged.filter(
            (path) => !path.includes(blockPath)
          )

          return newStagedWithoutThisBlock
        })
      } else {
        const onlyStagedPathIsRoot =
          stagedBlockPaths.length === 1 &&
          stagedBlockPaths[0] === treeNew?.root.value.id

        setStagedBlockPaths((prevStaged) => {
          // if the root was selected, then we stage everything but this one
          if (onlyStagedPathIsRoot) {
            return unstagePath(blockId, { forceStageOthers: true }) ?? []
          } else if (getInheritsStage(blockId) || getIsStaged(blockId)) {
            return unstagePath(blockId, { forceStageOthers: false }) ?? []
          }

          const prevStagedWithoutPathsOfChildrenOrAnyDescendants = (
            prevStaged ?? []
          ).filter((path) => !path.includes(blockPath))

          return [
            ...prevStagedWithoutPathsOfChildrenOrAnyDescendants,
            blockPath,
          ]
        })
      }
    },
    [
      getInheritsStage,
      getIsStaged,
      diff?.changesTree.root.value.id,
      getSourceBlockPath,
      setStagedBlockPaths,
      unstagePath,
      stagedBlockPaths,
      treeNew?.root.value.id,
    ]
  )

  const getStagedBlockIds = React.useCallback(
    ({
      fallbackToWipBlocks,
    }: {
      fallbackToWipBlocks?: boolean
    } = {}) => {
      if (stagedBlockPaths.length === 0) {
        return fallbackToWipBlocks
          ? [treeNew?.root.value.id].filter(Boolean)
          : []
      }

      return stagedBlockPaths.map((path) => path.split('/').pop()!)
    },
    [stagedBlockPaths, treeNew?.root.value.id]
  )

  const allChangesAreStaged = React.useMemo(() => {
    if (!diff?.changesTree.root.value) return false
    const rootBlock = diff.changesTree.root.value

    return stagedBlockPaths.includes(rootBlock.id) || allCheckboxesAreChecked
  }, [diff?.changesTree.root.value, stagedBlockPaths, allCheckboxesAreChecked])

  const filterBlockIdsByStage = React.useCallback(
    (
      show: 'staged' | 'unstaged',
      options?: Partial<{ includeNested: boolean; includeRoot: boolean }>
    ) => {
      const stagedBlocks = getStagedBlockIds()
      if (show === 'staged' && !options?.includeNested) return stagedBlocks
      if (show === 'unstaged' && allChangesAreStaged) return []

      if (!treeNewMethods?.getNode) return []
      const changedBlocks = Array.from(
        diff?.changesTree.itemsMap.values() ?? []
      ).reduce((acc, node) => {
        if (
          (options?.includeNested ||
            isBlockWithChildren(node.value) ||
            node.value.type === 'agent') &&
          (options?.includeRoot || !isRootBlock(node.value))
        ) {
          const blockId = idToSourceBlockId(node.value)
          return acc.concat(blockId)
        } else return acc
      }, [] as string[])

      if (show === 'staged') {
        // if we want nested, then we want to show all the children of the staged blocks
        return changedBlocks.filter((id) => {
          if (stagedBlocks.includes(id)) return true
          const path = getSourceBlockPath(id)
          return (
            path && stagedBlocks.some((stagedId) => path.includes(stagedId))
          )
        })
      } else {
        if (!stagedBlocks.length) return changedBlocks

        return changedBlocks.filter((id) => {
          if (stagedBlocks.includes(id)) return false
          const path = getSourceBlockPath(id)
          return (
            !path || !stagedBlocks.some((stagedId) => path.includes(stagedId))
          )
        })
      }
    },
    [
      getStagedBlockIds,
      allChangesAreStaged,
      treeNewMethods?.getNode,
      diff?.changesTree.itemsMap,
      getSourceBlockPath,
    ]
  )

  const getUnstagedBlockIds = React.useCallback(
    (
      options?: Partial<{
        nested: boolean
        includeRoot: boolean
      }>
    ) => filterBlockIdsByStage('unstaged', options),
    [filterBlockIdsByStage]
  )

  const allChangesAreUnstaged = React.useMemo(() => {
    if (!diff) return false
    return stagedBlockPaths.length === 0
  }, [diff, stagedBlockPaths])

  const stageAll = React.useCallback(() => {
    if (!treeNew?.root.value.id) return

    if (allCheckboxesAreChecked) return setStagedBlockPaths([])

    // add the root
    setStagedBlockPaths((prevState) =>
      prevState?.includes(treeNew.root.value.id) ? [] : [treeNew.root.value.id]
    )
  }, [treeNew?.root.value.id, setStagedBlockPaths, allCheckboxesAreChecked])

  const resetStagingTree = React.useCallback(() => {
    if (!treeNew?.root.value.id) return
    return setStagedBlockPaths([treeNew.root.value.id])
  }, [treeNew?.root.value.id, setStagedBlockPaths])

  React.useEffect(() => {
    if (!diff?.changesTree) return
    const changesTreeList = document.querySelector('[data-tree-list]')

    const timeout = setTimeout(() => {
      const stageCheckboxList =
        changesTreeList?.querySelectorAll<HTMLButtonElement>(
          '[role=checkbox][data-stage-control="true"]'
        )

      if (!stageCheckboxList?.length) return

      setAllCheckboxesAreChecked(
        Array.from(stageCheckboxList ?? []).every(
          (checkbox) => checkbox.ariaChecked === 'true'
        )
      )
    })

    return () => clearTimeout(timeout)
  }, [stagedBlockPaths, diff?.changesTree])

  const state: StageControls = React.useMemo(() => {
    if (isGuestInOthersBranch) {
      return {
        allChangesAreStaged: true,
        allChangesAreUnstaged: false,
        stagedBlockPaths: [treeNew?.root.value.id].filter(Boolean),
        getStagedBlockIds: () => [treeNew?.root.value.id].filter(Boolean),
        filterBlockIdsByStage: () => [treeNew?.root.value.id].filter(Boolean),
        getUnstagedBlockIds: () => [],
        getIsIndeterminate: () => false,
        getIsStaged: () => true,
        stageBlock: () => undefined,
        getInheritsStage: () => false,
        stageAll: () => undefined,
        resetStagingTree,
        unstageAll: () => undefined,
        enabled: false,
      }
    }

    return {
      allChangesAreStaged,
      allChangesAreUnstaged,
      stagedBlockPaths,
      getStagedBlockIds,
      getIsIndeterminate,
      getIsStaged,
      stageBlock,
      stageAll,
      resetStagingTree,
      getInheritsStage,
      filterBlockIdsByStage,
      getUnstagedBlockIds,
      unstageAll: () => setStagedBlockPaths([]),
      enabled: true,
    }
  }, [
    allChangesAreStaged,
    allChangesAreUnstaged,
    getInheritsStage,
    getIsIndeterminate,
    getIsStaged,
    getStagedBlockIds,
    filterBlockIdsByStage,
    getUnstagedBlockIds,
    isGuestInOthersBranch,
    resetStagingTree,
    setStagedBlockPaths,
    stageAll,
    stageBlock,
    stagedBlockPaths,
    treeNew?.root.value.id,
  ])

  const storeRef = React.useRef(createStagedBlocksStore(state))

  // keep store in sync with the result
  React.useEffect(() => {
    if (!state) return
    storeRef.current.setState(state)
  }, [state])

  return (
    // eslint-disable-next-line react-compiler/react-compiler
    <StagedBlocksContext.Provider value={storeRef.current}>
      {children}
    </StagedBlocksContext.Provider>
  )
}

export function useStagedControls<T>(
  selector: (state: StageControls) => T,
  compare?: (prev: T, next: T) => boolean
): T {
  const store = React.useContext(StagedBlocksContext)
  if (!store) {
    throw new Error('Missing StagedBlocksContext.Provider in the tree')
  }
  return useStoreWithEqualityFn(store, selector, compare)
}
